import { Button } from '@finpay-development/shared-components';
import { Grid, Typography, Button as MUIButton, Box } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import moment from 'moment';
import { formatNumberToUSD } from 'src/shared/calculators';
import type { PaymentProgramViewContext } from './use-payment-program-view';

interface OptimalPaymentViewProps {
  ctx: PaymentProgramViewContext;
}

export function OptimalPaymentView({ ctx }: OptimalPaymentViewProps) {
  const {
    isMobile,
    matchingRule,
    pfrAmt,
    pfrCollected,
    optDownPayment,
    optimalTermNumDisplay,
    recurringAmt,
    billingFrequency,
    isLoadingEncounter,
    isLoadingRule,
    handleBack,
    handleSelectProgram,
    handleSelectOther,
    classes,
  } = ctx;

  const isWeekly = billingFrequency === 'weekly';
  const termUnitSingular = isWeekly ? 'week' : 'month'; // "3 month program" / "3 week program"
  const termLabel = isWeekly ? (optimalTermNumDisplay > 1 ? 'weeks' : 'week') : (optimalTermNumDisplay > 1 ? 'months' : 'month');
  const perPeriod = isWeekly ? 'per week' : 'per month';
  const eachPeriod = isWeekly ? 'each week' : 'each month';

  const isLoading = isLoadingEncounter || isLoadingRule;
  const hasRuleData =
    matchingRule &&
    matchingRule.ruleId !== undefined &&
    matchingRule.ruleId !== null &&
    matchingRule.ruleId !== -1;

  if (isLoading || !hasRuleData) {
    return (
      <Grid item xs={12}>
        <Grid container className="mt-4" justifyContent="center">
          <Grid item xs={12}>
            <Typography variant="body1" textAlign="center">
              Loading payment program options...
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Grid container className="mt-4">
        <Typography variant="body2" textAlign="left" sx={{ mb: 5, fontSize: '2rem', lineHeight: '2.6rem' }}>
          No problem, your second option is an interest free installment program. Many people I speak with choose
          this automated program because it is a low cost way to spread your payments over time.
          <br />
          <br />
          Most people in your situation, pick the{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {optimalTermNumDisplay} {termUnitSingular}
          </Box>{' '}
          program with a down payment of{' '}
          {matchingRule?.optimalDownPmtType === '%'
            ? `${matchingRule?.optimalDownPmt || 0}%`
            : formatNumberToUSD(optDownPayment)}
          . In your case, you would make a down payment today of{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {formatNumberToUSD(optDownPayment)}
          </Box>
          , of which you've already paid{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {formatNumberToUSD(pfrCollected)}
          </Box>
          . The remaining payments would be spread over {optimalTermNumDisplay} {termLabel} at{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {formatNumberToUSD(recurringAmt)}
          </Box>{' '}
          {perPeriod}.{' '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            Does this option work for you?
          </Box>
        </Typography>
        <Grid container direction="row" className="table-header">
          <Grid item xs={4} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="left">
              Down Payment
            </Typography>
          </Grid>
          <Grid item xs={4} className="mt-2">
            <Typography variant="subtitle2" textAlign="center">
              Term
            </Typography>
          </Grid>
          <Grid item xs={4} className="mt-2 pr-2">
            <Typography variant="subtitle2" textAlign="right">
              Recurring Amount
            </Typography>
          </Grid>
        </Grid>
        <Grid container direction="row">
          <Grid item xs={4} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="left">
              {formatNumberToUSD(optDownPayment)}
            </Typography>
          </Grid>
          <Grid item xs={4} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="center">
              {optimalTermNumDisplay} {termLabel}
            </Typography>
          </Grid>
          <Grid item xs={4} className="mt-2 pl-2">
            <Typography variant="subtitle2" textAlign="right">
              {formatNumberToUSD(recurringAmt)}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      {isMobile && (
        <Grid container direction="row" justifyContent="center">
          <Typography variant="subtitle2" className={classes.fullWidth} textAlign="center">
            The first recurring payment will occur on {moment().add(1, isWeekly ? 'weeks' : 'months').format('MM/DD/YYYY')} and {eachPeriod}
            thereafter until the balance is paid in full
          </Typography>
        </Grid>
      )}
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={2} className="mt-2">
          <MUIButton
            onClick={handleBack}
            variant="outlined"
            fullWidth
            test-id="payment-program-optimal-back"
            sx={{
              backgroundColor: 'white',
              borderColor: '#e0e0e0',
              color: '#000',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#bdbdbd',
              },
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> Back
          </MUIButton>
        </Grid>
        <Grid item xs={12} md={5} className="mt-2">
          <Button onClick={handleSelectProgram} fullWidth test-id="payment-program-optimal-add-payment">
            Add Payment Details
          </Button>
        </Grid>
        <Grid item xs={12} md={5} className="mt-2">
          <MUIButton
            onClick={handleSelectOther}
            fullWidth
            variant="text"
            sx={{ textTransform: 'none' }}
            test-id="payment-program-optimal-cant-afford"
          >
            Can't afford?
          </MUIButton>
        </Grid>
      </Grid>
    </Grid>
  );
}
